import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocale } from "../i18n";

const SITE_URL = "https://wowromania.com";
const OG_IMAGE = "https://images.pexels.com/photos/13713831/pexels-photo-13713831.jpeg";

/**
 * SEO — unified <head> block per page.
 * Renders: title, description, canonical, hreflang, OpenGraph, Twitter, JSON-LD.
 *
 * Props:
 *   title       — page-specific title (auto-suffixed with " · WowRomania")
 *   description — meta description (~155 chars)
 *   path        — path portion of URL (e.g. "/tools/currency"), used for canonical + og:url
 *   image       — optional OG image URL (defaults to hero image)
 *   type        — og:type (default "website"; use "article" for editorial pages)
 *   noindex     — if true, sets robots to noindex,nofollow
 *   jsonLd      — array of JSON-LD objects to inject as <script type="application/ld+json">
 */
export default function SEO({
  title,
  exactTitle,
  description,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd = [],
}) {
  const { locale } = useLocale();
  const canonical = `${SITE_URL}${path}`;
  const fullTitle = exactTitle
    ? exactTitle
    : (title ? `${title} · WowRomania` : "WowRomania · The reference travel platform for Romania");
  const ogLocale = locale === "ro" ? "ro_RO" : "en_US";
  const altLocale = locale === "ro" ? "en_US" : "ro_RO";

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "WowRomania",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.ico`,
    sameAs: [],
    description: "The reference travel platform for Romania.",
  };

  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large"} />
      <link rel="canonical" href={canonical} />

      {/* hreflang — same URL for both locales (locale is JS-toggled) */}
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="ro" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="WowRomania" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Organization always present */}
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export const SITE = { url: SITE_URL, name: "WowRomania" };
